import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
  findTable,
  TableMap,
  CellSelection,
} from '@tiptap/pm/tables';
import { NodeSelection } from '@tiptap/pm/state';
import { Plus, Trash2, GripHorizontal } from 'lucide-react';

/**
 * Section-rooted overlay anchored to the active table — gutter handles,
 * hover-insert affordances, select-table corner, delete-table corner.
 *
 * **Not** a TipTap NodeView. Wrapping `<table>` in a NodeView would
 * re-implement prosemirror-tables' `TableView` + `columnResizing` plugin and
 * de-syncs collab; this overlay only observes editor state and dispatches
 * commands, never replaces the table's DOM. See vault CLAUDE.md.
 *
 * Hidden below 768px (touch-target ergonomics — bubble menu covers touch).
 * Hidden entirely when `!editor.isEditable` (viewers see nothing).
 */
interface TableChromeInfo {
  /** Wrapper rect (the `.tableWrapper` from `columnResizing`) — chrome anchors to this. */
  wrapperRect: DOMRect;
  /** Wrapper's `scrollLeft` — gutters/trash subtract this so they track when the
   * wrapper is scrolled horizontally (wide tables on narrow viewports). */
  wrapperScrollLeft: number;
  /** Column widths in pixels, source-of-truth from the table's `<colgroup>`. */
  colWidths: number[];
  /** Row heights + tops in pixels, from each `<tr>`'s offsetHeight / offsetTop. */
  rowMetrics: { top: number; height: number }[];
  /** PM position of the table node. */
  tablePos: number;
  /** Structural map for cell-position lookups. */
  map: TableMap;
}

export function TableChrome({ editor }: { editor: Editor }) {
  const [info, setInfo] = useState<TableChromeInfo | null>(null);
  const [wide, setWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  );

  // Track viewport ≥768px; below that we hide the chrome and rely on the
  // bubble menu (which has all the same ops in a touch-friendly form).
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setWide(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!editor) return;
    let raf = 0;
    // Hoisted options so add and remove pass the SAME object reference —
    // Safari is strict about matching options when removing passive listeners.
    const passiveOpts: AddEventListenerOptions = { passive: true };
    // Track the currently-bound DOM targets so we only re-bind listeners /
    // observers when those targets actually change. Previously this update()
    // disconnected+reconnected the ResizeObserver and re-added the wrapper
    // scroll listener every keystroke, which thrashed.
    let scrollAncestor: HTMLElement | null = null;
    let observedTableDom: HTMLElement | null = null;
    let observedWrapperEl: HTMLElement | null = null;
    let resizeObs: ResizeObserver | null = null;
    // Cache of the last-rendered table node — most update() calls during a
    // typing burst hit the SAME table, so we can skip the structural
    // re-introspection (TableMap.get, colgroup walk, row offsets) when the
    // node identity is unchanged. Editor transactions inside cells DO change
    // the node identity (PM is immutable), so this cache invalidates exactly
    // when the structure could have changed.
    let lastTableNode: any = null;
    let cachedColWidths: number[] = [];
    let cachedRowMetrics: { top: number; height: number }[] = [];
    let cachedMap: TableMap | null = null;

    function findScrollAncestor(el: HTMLElement | null): HTMLElement | null {
      let cur = el?.parentElement ?? null;
      while (cur) {
        const cs = getComputedStyle(cur);
        if (
          /(auto|scroll|overlay)/.test(cs.overflowY) ||
          /(auto|scroll|overlay)/.test(cs.overflowX)
        ) {
          return cur;
        }
        cur = cur.parentElement;
      }
      return null;
    }

    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!editor.isEditable) {
          setInfo(null);
          return;
        }
        const { state, view } = editor;
        let found;
        try {
          found = findTable(state.selection.$from);
        } catch {
          found = null;
        }
        if (!found) {
          setInfo(null);
          return;
        }
        const tableDom = view.nodeDOM(found.pos) as HTMLTableElement | null;
        if (!tableDom || tableDom.tagName !== 'TABLE') {
          setInfo(null);
          return;
        }
        // The wrapper is the `.tableWrapper` div prosemirror-tables wraps the
        // table in when `resizable: true`. We anchor against the wrapper so
        // horizontal scroll inside it doesn't desync the gutters.
        const wrapperEl =
          (tableDom.parentElement as HTMLElement | null) ?? tableDom;

        // Re-bind ResizeObserver only when the observed DOM nodes change.
        if (
          tableDom !== observedTableDom ||
          wrapperEl !== observedWrapperEl
        ) {
          if (resizeObs) resizeObs.disconnect();
          resizeObs = new ResizeObserver(update);
          resizeObs.observe(tableDom);
          if (wrapperEl !== tableDom) resizeObs.observe(wrapperEl);
          observedTableDom = tableDom;
        }

        // Re-bind the wrapper's scroll listener only when the wrapper changes.
        if (wrapperEl !== observedWrapperEl) {
          if (observedWrapperEl) {
            observedWrapperEl.removeEventListener('scroll', update, passiveOpts);
          }
          wrapperEl.addEventListener('scroll', update, passiveOpts);
          observedWrapperEl = wrapperEl;
        }

        // Re-bind scroll-ancestor listener only when the ancestor changes.
        const newScrollAncestor = findScrollAncestor(tableDom);
        if (newScrollAncestor !== scrollAncestor) {
          if (scrollAncestor)
            scrollAncestor.removeEventListener('scroll', update, passiveOpts);
          scrollAncestor = newScrollAncestor;
          if (scrollAncestor)
            scrollAncestor.addEventListener('scroll', update, passiveOpts);
        }

        // Heavy DOM introspection only when the table's PM node identity has
        // changed (structural edit / column resize / cell text edit). For
        // pure scroll / window-resize events, reuse the cache and just
        // refresh wrapperRect + scrollLeft below.
        let colWidths: number[];
        let rowMetrics: { top: number; height: number }[];
        let map: TableMap;
        if (found.node !== lastTableNode) {
          const cols = Array.from(
            tableDom.querySelectorAll('colgroup > col'),
          ) as HTMLTableColElement[];
          const firstRow = tableDom.rows[0];
          colWidths = cols.length
            ? cols.map((c) => parseFloat(c.style.width) || c.offsetWidth || 0)
            : firstRow
              ? Array.from(firstRow.cells).map(
                  (cell) => (cell as HTMLElement).offsetWidth,
                )
              : [];
          rowMetrics = Array.from(tableDom.rows).map((tr) => ({
            top: (tr as HTMLElement).offsetTop,
            height: (tr as HTMLElement).offsetHeight,
          }));
          map = TableMap.get(found.node);
          lastTableNode = found.node;
          cachedColWidths = colWidths;
          cachedRowMetrics = rowMetrics;
          cachedMap = map;
        } else {
          colWidths = cachedColWidths;
          rowMetrics = cachedRowMetrics;
          map = cachedMap!; // truthy whenever lastTableNode is set
        }

        const wrapperRect = wrapperEl.getBoundingClientRect();
        const wrapperScrollLeft = wrapperEl.scrollLeft;

        setInfo({
          wrapperRect,
          wrapperScrollLeft,
          colWidths,
          rowMetrics,
          tablePos: found.pos,
          map,
        });
      });
    };

    editor.on('selectionUpdate', update);
    editor.on('transaction', update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, passiveOpts);
    update();

    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, passiveOpts);
      if (scrollAncestor)
        scrollAncestor.removeEventListener('scroll', update, passiveOpts);
      if (observedWrapperEl)
        observedWrapperEl.removeEventListener('scroll', update, passiveOpts);
      if (resizeObs) resizeObs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [editor]);

  if (!info || !wide) return null;

  const { wrapperRect, wrapperScrollLeft, colWidths, rowMetrics } = info;

  /** Re-resolve the table at click time. The render-closure values
   * (`info.tablePos`, `info.map`) can be stale by the time the click fires —
   * a peer transaction between render and click could have shifted positions.
   * Cheap to recompute and eliminates the race. Returns null if the cursor
   * is no longer in a table. */
  function resolveTable(): {
    tableStart: number;
    map: TableMap;
    tablePos: number;
  } | null {
    try {
      const found = findTable(editor.state.selection.$from);
      if (!found) return null;
      return {
        tableStart: found.pos + 1,
        map: TableMap.get(found.node),
        tablePos: found.pos,
      };
    } catch {
      return null;
    }
  }

  function selectColumn(colIdx: number) {
    try {
      const t = resolveTable();
      if (!t) return;
      const top = t.map.map[colIdx];
      const bottom = t.map.map[(t.map.height - 1) * t.map.width + colIdx];
      if (top === undefined || bottom === undefined) return;
      const $anchor = editor.state.doc.resolve(t.tableStart + top);
      const $head = editor.state.doc.resolve(t.tableStart + bottom);
      const sel = CellSelection.colSelection($anchor, $head);
      editor.view.dispatch(editor.state.tr.setSelection(sel));
      editor.view.focus();
    } catch {
      /* selection out of range — table mutated mid-click */
    }
  }

  function selectRow(rowIdx: number) {
    try {
      const t = resolveTable();
      if (!t) return;
      const left = t.map.map[rowIdx * t.map.width];
      const right = t.map.map[rowIdx * t.map.width + (t.map.width - 1)];
      if (left === undefined || right === undefined) return;
      const $anchor = editor.state.doc.resolve(t.tableStart + left);
      const $head = editor.state.doc.resolve(t.tableStart + right);
      const sel = CellSelection.rowSelection($anchor, $head);
      editor.view.dispatch(editor.state.tr.setSelection(sel));
      editor.view.focus();
    } catch {
      /* table mutated mid-click */
    }
  }

  function insertColumnAfter(colIdx: number) {
    try {
      const t = resolveTable();
      if (!t) return;
      const cellPos = t.map.map[colIdx];
      if (cellPos === undefined) return;
      const $cell = editor.state.doc.resolve(t.tableStart + cellPos);
      editor.view.dispatch(
        editor.state.tr.setSelection(new CellSelection($cell)),
      );
      editor.chain().focus().addColumnAfter().run();
    } catch {
      /* ignore */
    }
  }

  function insertRowAfter(rowIdx: number) {
    try {
      const t = resolveTable();
      if (!t) return;
      const cellPos = t.map.map[rowIdx * t.map.width];
      if (cellPos === undefined) return;
      const $cell = editor.state.doc.resolve(t.tableStart + cellPos);
      editor.view.dispatch(
        editor.state.tr.setSelection(new CellSelection($cell)),
      );
      editor.chain().focus().addRowAfter().run();
    } catch {
      /* ignore */
    }
  }

  function selectTable() {
    try {
      const t = resolveTable();
      if (!t) return;
      editor.view.dispatch(
        editor.state.tr.setSelection(
          NodeSelection.create(editor.state.doc, t.tablePos),
        ),
      );
      editor.view.focus();
    } catch {
      /* ignore */
    }
  }

  // Geometry: chrome is `position: fixed`. Top column gutter sits ABOVE the
  // wrapper's top edge; left row gutter sits to the LEFT of the wrapper's
  // left edge. Corners overlap the wrapper's outer corners.
  const GUTTER = 12; // px — bar thickness
  const colGutterTop = wrapperRect.top - GUTTER - 2;
  const rowGutterLeft = wrapperRect.left - GUTTER - 2;

  let xCursor = 0;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 30,
      }}
    >
      {/* TOP COLUMN GUTTER. Subtract `wrapperScrollLeft` so when the wrapper
          scrolls horizontally (wide tables on narrow viewports) the gutters
          track their columns instead of staying pinned to the wrapper's left. */}
      {colWidths.map((w, i) => {
        const left = wrapperRect.left + xCursor - wrapperScrollLeft;
        xCursor += w;
        return (
          <div
            key={`cg-${i}`}
            className="tt-col-gutter"
            data-popover-trigger
            style={{
              position: 'fixed',
              top: colGutterTop,
              left,
              width: w,
              height: GUTTER,
              pointerEvents: 'auto',
            }}
            onClick={() => selectColumn(i)}
            title={`Select column ${i + 1}`}
          >
            <GripHorizontal size={10} className="opacity-60" />
            <button
              type="button"
              className="tt-gap-insert tt-gap-col"
              style={{ right: -8 }}
              onClick={(e) => {
                e.stopPropagation();
                insertColumnAfter(i);
              }}
              title="Insert column right"
            >
              <Plus size={10} />
            </button>
          </div>
        );
      })}

      {/* LEFT ROW GUTTER */}
      {rowMetrics.map((rm, i) => {
        return (
          <div
            key={`rg-${i}`}
            className="tt-row-gutter"
            data-popover-trigger
            style={{
              position: 'fixed',
              top: wrapperRect.top + rm.top,
              left: rowGutterLeft,
              width: GUTTER,
              height: rm.height,
              pointerEvents: 'auto',
            }}
            onClick={() => selectRow(i)}
            title={`Select row ${i + 1}`}
          >
            <GripHorizontal size={10} className="rotate-90 opacity-60" />
            <button
              type="button"
              className="tt-gap-insert tt-gap-row"
              style={{ bottom: -8 }}
              onClick={(e) => {
                e.stopPropagation();
                insertRowAfter(i);
              }}
              title="Insert row below"
            >
              <Plus size={10} />
            </button>
          </div>
        );
      })}

      {/* TOP-LEFT corner — select whole table */}
      <button
        type="button"
        data-popover-trigger
        className="tt-table-corner"
        style={{
          position: 'fixed',
          top: colGutterTop,
          left: rowGutterLeft,
          width: GUTTER,
          height: GUTTER,
          pointerEvents: 'auto',
        }}
        onClick={selectTable}
        title="Select whole table"
      />

      {/* BOTTOM-RIGHT corner — delete table */}
      <button
        type="button"
        data-popover-trigger
        className="tt-table-trash"
        style={{
          position: 'fixed',
          top: wrapperRect.bottom + 4,
          left: wrapperRect.right - 22,
          pointerEvents: 'auto',
        }}
        onClick={() => editor.chain().focus().deleteTable().run()}
        title="Delete table"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}
